import { useParams, Navigate, Link } from "react-router-dom";
import { Star, ArrowUpRight } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { CASE_STUDIES, getCaseStudyBySlug } from "@/data/caseStudies";
import { COMPANY } from "@/data/clientsData";
import { organizationSchema, breadcrumbSchema, itemListSchema, SITE_URL } from "@/lib/seo-schemas";
import { SeoHero, SeoSection, CheckList, QuoteBand } from "@/components/SeoBlocks";
import { thumb, absUrl } from "@/lib/img";

const INDEX_SEO = {
  title: "Exhibition Stand Case Studies | Stagehook Global",
  description: "Client-reviewed exhibition stand projects: SMM Hamburg, AAPEX Las Vegas, SupplySide West, Prodexpo and Analitika Expo Moscow, with the brief, what we delivered and photos.",
  path: "/case-studies",
  jsonLd: [organizationSchema(), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Case studies", path: "/case-studies" }]), itemListSchema("Exhibition stand case studies", CASE_STUDIES.map((c) => ({ name: c.title, path: `/case-studies/${c.slug}` })))],
};

const caseSchema = (c) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: c.title,
  description: c.brief,
  image: c.images.map(absUrl),
  author: { "@id": `${SITE_URL}/#organization` },
  publisher: { "@id": `${SITE_URL}/#organization` },
  mainEntityOfPage: `${SITE_URL}/case-studies/${c.slug}`,
  about: { "@type": "Event", name: c.expoData?.name, location: { "@type": "Place", name: c.expoData?.venue, address: `${c.expoData?.city}, ${c.expoData?.countryName}` } },
  review: { "@type": "Review", author: { "@type": "Organization", name: c.quote.author }, reviewBody: c.quote.text, reviewRating: { "@type": "Rating", ratingValue: c.quote.rating, bestRating: 5 }, itemReviewed: { "@type": "Organization", name: COMPANY.name, "@id": `${SITE_URL}/#organization` } },
});

export const CaseCard = ({ c }) => (
  <Link to={`/case-studies/${c.slug}`} data-testid={`case-card-${c.slug}`} className="shg-card group block">
    <div className="aspect-[16/10] overflow-hidden"><img src={thumb(c.images[0])} alt={`${c.client} exhibition stand at ${c.expoData?.name}`} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
    <div className="p-6">
      <div className="font-mono text-[10px] tracking-[0.2em] text-[var(--text-muted)] mb-2">{c.quote.expo.toUpperCase()}</div>
      <h3 className="font-display text-xl mb-3 group-hover:text-[var(--red)] transition-colors">{c.title}</h3>
      <div className="flex items-center gap-1.5 text-[var(--red)] text-sm font-medium">Read case study <ArrowUpRight size={15} /></div>
    </div>
  </Link>
);

const CaseIndex = () => (
  <main data-testid="case-index-page" className="pb-24">
    <SeoHero eyebrow="Client-reviewed projects" title="Exhibition stand" accent="case studies." intro="Real stands, real client quotes. The brief, what we delivered and photos from the show floor." />
    <section className="py-6"><div className="shg-container grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{CASE_STUDIES.map((c) => <CaseCard key={c.slug} c={c} />)}</div></section>
    <QuoteBand source="case-index" />
  </main>
);

const CaseStudyPage = () => {
  const { slug } = useParams();
  const c = slug ? getCaseStudyBySlug(slug) : null;
  useSEO(slug ? {
    title: c ? `${c.client} at ${c.quote.expo} — Case Study | Stagehook` : "Case study | Stagehook Global",
    description: c ? `${c.brief} See what Stagehook Global delivered and what ${c.client} said.` : undefined,
    image: c?.images[0],
    path: `/case-studies/${slug}`,
    jsonLd: c ? [organizationSchema(), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Case studies", path: "/case-studies" }, { name: c.client, path: `/case-studies/${c.slug}` }]), caseSchema(c)] : undefined,
  } : INDEX_SEO);

  if (!slug) return <CaseIndex />;
  if (!c) return <Navigate to="/404" replace />;
  const others = CASE_STUDIES.filter((x) => x.slug !== c.slug).slice(0, 3);

  return (
    <main data-testid="case-study-page" className="pb-24">
      <SeoHero eyebrow={`Case study · ${c.quote.expo}`} title={c.title} intro={c.brief} chips={[c.stand, c.expoData?.industry || c.expoData?.clients?.[0]?.industry].filter(Boolean)} />
      <section className="py-6"><div className="shg-container grid md:grid-cols-2 gap-4">
        {c.images.slice(0, 4).map((src, i) => <div key={src} className={`overflow-hidden rounded-2xl ${i === 0 ? "md:col-span-2 aspect-[21/9]" : "aspect-[4/3]"}`}><img src={i === 0 ? src : thumb(src)} alt={`${c.client} stand at ${c.expoData?.name}, ${c.expoData?.city} — photo ${i + 1}`} loading={i === 0 ? "eager" : "lazy"} fetchPriority={i === 0 ? "high" : undefined} decoding="async" data-testid={`case-img-${i}`} className="w-full h-full object-cover" /></div>)}
      </div></section>
      <SeoSection title="What we delivered" testId="case-delivered"><CheckList items={c.delivered} /></SeoSection>
      <SeoSection title="What the client said" testId="case-quote">
        <figure className="shg-card p-8 max-w-3xl">
          <div className="flex gap-0.5 mb-4">{[1, 2, 3, 4, 5].map((i) => <Star key={i} size={16} fill={i <= c.quote.rating ? "var(--red)" : "none"} className="text-[var(--red)]" />)}</div>
          <blockquote className="text-xl leading-relaxed">“{c.quote.text}”</blockquote>
          <figcaption className="mt-4 text-sm text-[var(--text-muted)]">{c.quote.author} · {c.quote.expo}</figcaption>
        </figure>
      </SeoSection>
      <SeoSection title="Project page"><Link to={`/expo/${c.expo}`} data-testid="case-expo-link" className="text-[var(--red)]">{c.expoData?.name} stand builder — {c.expoData?.city} →</Link></SeoSection>
      <QuoteBand expoName={c.expoData?.name} source="case-study" />
      <SeoSection title="More case studies"><div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">{others.map((o) => <CaseCard key={o.slug} c={o} />)}</div></SeoSection>
    </main>
  );
};

export default CaseStudyPage;
